import { useState } from 'react'
import { makeAssistantToolUI, type ToolCallMessagePartProps } from '@assistant-ui/react'

import { toMinor } from '../lib/money'

type PayingAccount = { id: string; label: string }

type ConfigureCardArgs = {
  cardId: string
  name: string
  statementDay: number | null
  paymentDay: number | null
  balance: string | null
  expectedCharge: string | null
  missing: string[]
  accounts: PayingAccount[]
}

type ConfigureCardResult = {
  cardId: string
  later?: boolean
  payingAccountId: string | null
  paymentAmountKind?: 'STATEMENT_BALANCE' | 'MINIMUM' | 'FIXED'
  paymentAmountMinor?: number | null
  statementDay?: number
  paymentDay?: number
}

/**
 * How a card gets paid, and when its cycle turns over.
 *
 * Three things a card cannot be projected without. The two dates usually come from the
 * bank and are prefilled — shown, not hidden, because a projected payment traceable to
 * "your bank said the 28th" is checkable and one traceable to nothing is not. How it is
 * paid never comes from the bank and always has to be asked.
 *
 * Paying by hand is a real answer rather than a refusal to answer: such a card contributes
 * nothing to the projection, correctly, because the payment shows up as whatever cash flow
 * the user records instead. So is "I'll do this later" — the card stays in the plan,
 * visibly unfinished, and the conversation moves on rather than holding everything hostage
 * to a detail somebody has to go and look up.
 */
export const ConfigureCardToolUI = makeAssistantToolUI<ConfigureCardArgs, ConfigureCardResult>({
  toolName: 'configureCard',
  render: ConfigureCardTool,
})

const MANUAL = '__manual__'

function ConfigureCardTool({
  args,
  status,
  result,
  addResult,
}: ToolCallMessagePartProps<ConfigureCardArgs, ConfigureCardResult>) {
  const accounts = args.accounts ?? []
  const [statementDay, setStatementDay] = useState(
    args.statementDay ? String(args.statementDay) : '',
  )
  const [paymentDay, setPaymentDay] = useState(args.paymentDay ? String(args.paymentDay) : '')
  const [paidFrom, setPaidFrom] = useState(accounts[0]?.id ?? MANUAL)
  const [howMuch, setHowMuch] = useState<'STATEMENT_BALANCE' | 'MINIMUM' | 'FIXED'>(
    'STATEMENT_BALANCE',
  )
  const [fixedAmount, setFixedAmount] = useState('')
  const waiting = status.type === 'requires-action'

  if (result) {
    return (
      <p className="aui-note">
        {result.later ? `Left ${args.name} for later.` : `Set up ${args.name}.`}
      </p>
    )
  }

  const manual = paidFrom === MANUAL
  const statement = Number(statementDay)
  const payment = Number(paymentDay)
  const fixedMinor = howMuch === 'FIXED' ? toMinor(fixedAmount, 'USD') : null

  const datesOk =
    Number.isInteger(statement) &&
    statement >= 1 &&
    statement <= 31 &&
    Number.isInteger(payment) &&
    payment >= 1 &&
    payment <= 31
  // Paying by hand needs no cycle: the card moves no money, so there is nothing to schedule.
  const valid = manual || (datesOk && (howMuch !== 'FIXED' || fixedMinor !== null))

  return (
    <form
      className="aui-form"
      onSubmit={(event) => {
        event.preventDefault()
        if (!valid) return
        addResult({
          cardId: args.cardId,
          payingAccountId: manual ? null : paidFrom,
          paymentAmountKind: manual ? undefined : howMuch,
          paymentAmountMinor: howMuch === 'FIXED' ? fixedMinor : null,
          statementDay: manual ? undefined : statement,
          paymentDay: manual ? undefined : payment,
        })
      }}
    >
      {args.balance && (
        <p className="aui-note">
          {args.balance} owed right now
          {args.expectedCharge ? `, and about ${args.expectedCharge} a cycle` : ''}.
        </p>
      )}

      <label className="aui-field">
        <span>Paid from</span>
        <select value={paidFrom} onChange={(e) => setPaidFrom(e.target.value)} disabled={!waiting}>
          {accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.label}
            </option>
          ))}
          <option value={MANUAL}>I pay it myself</option>
        </select>
      </label>

      {!manual && (
        <>
          <label className="aui-field">
            <span>How much</span>
            <select
              value={howMuch}
              onChange={(e) => setHowMuch(e.target.value as typeof howMuch)}
              disabled={!waiting}
            >
              <option value="STATEMENT_BALANCE">The whole statement</option>
              <option value="FIXED">A fixed amount</option>
              <option value="MINIMUM">Only the minimum</option>
            </select>
          </label>

          {howMuch === 'FIXED' && (
            <label className="aui-field">
              <span>Amount each cycle (USD)</span>
              <input
                value={fixedAmount}
                onChange={(e) => setFixedAmount(e.target.value)}
                inputMode="decimal"
                placeholder="250.00"
                disabled={!waiting}
              />
            </label>
          )}

          {howMuch === 'MINIMUM' && (
            <p className="aui-note">
              Paying the minimum means carrying a balance. Fluence can't project that yet, so
              the card will be saved but stay out of the forecast.
            </p>
          )}

          <div className="aui-field-row">
            <label className="aui-field">
              <span>Statement closes on the</span>
              <input
                value={statementDay}
                onChange={(e) => setStatementDay(e.target.value)}
                inputMode="numeric"
                placeholder="28"
                disabled={!waiting}
              />
            </label>
            <label className="aui-field">
              <span>Payment due on the</span>
              <input
                value={paymentDay}
                onChange={(e) => setPaymentDay(e.target.value)}
                inputMode="numeric"
                placeholder="15"
                disabled={!waiting}
              />
            </label>
          </div>
        </>
      )}

      <div className="aui-action">
        <button type="submit" className="btn-primary" disabled={!valid || !waiting}>
          {manual ? 'Save as paid by hand' : 'Add card'}
        </button>
        <button
          type="button"
          className="btn-ghost"
          disabled={!waiting}
          onClick={() =>
            addResult({ cardId: args.cardId, later: true, payingAccountId: null })
          }
        >
          I'll do this later
        </button>
      </div>
    </form>
  )
}
